import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import Card from './Card';
import { Card as CardData } from '../types';
import { isTouchDevice } from '../services/device';
import { getCardDisplayNameForCard, EssenceIcon, ShieldIcon } from '../constants';
import { Language } from '../data/translations';

interface TouchCardWrapperProps {
    card: CardData;
    rotation?: number;
    source?: 'hand' | 'prepared' | 'prepared-reaction';
    isInteractive?: boolean;
    isPlayable?: boolean;
    locked?: boolean;
    isTargetable?: boolean; // For cards requiring arrow targeting (Attack, Disease, etc.)
    onRotationChange?: (cardId: string, newRotation: number) => void;
    onDragStartApp?: (card: CardData, rotation: number, source: string) => void;
    onDragEndApp?: () => void;
    onTouchDrop?: (card: CardData, rotation: number, dropX: number, dropY: number) => void;
    onDoubleClick?: (cardId: string, e: React.MouseEvent<HTMLDivElement>) => void;
    // Touch targeting callbacks (for cards like Attack/Disease that need arrow)
    onTouchTargetStart?: (startRect: DOMRect) => void;
    onTouchTargetMove?: (x: number, y: number) => void;
    onTouchTargetEnd?: (dropX: number, dropY: number) => void;
    onTouchTargetCancel?: () => void;
    // Language for localization
    language?: Language;
    // Pass-through props to Card
    [key: string]: any;
}

/**
 * Wrapper component that adds touch-specific gestures to Card on touch devices:
 * - Long press (500ms) to start dragging
 * - Second finger while dragging toggles 180° rotation (listens at document level)
 * - Double-tap support
 * - Renders a custom drag clone instead of native drag image (fixes Apple issue)
 * 
 * On non-touch devices, this just renders Card with its normal behavior.
 */
const TouchCardWrapper: React.FC<TouchCardWrapperProps> = ({
    card,
    rotation = 0,
    source,
    isInteractive = false,
    isPlayable = true,
    locked = false,
    isTargetable = false,
    onRotationChange,
    onDragStartApp,
    onDragEndApp,
    onTouchDrop,
    onDoubleClick,
    onTouchTargetStart,
    onTouchTargetMove,
    onTouchTargetEnd,
    onTouchTargetCancel,
    language = 'en',
    ...cardProps
}) => {
    const isTouch = useMemo(() => isTouchDevice(), []);

    // Touch gesture state
    const [isTouchDragging, setIsTouchDragging] = useState(false);
    const [touchRotation, setTouchRotation] = useState(rotation);
    const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
    const [showTouchPreview, setShowTouchPreview] = useState(false);

    const longPressTimerRef = useRef<number | null>(null);
    const previewTimerRef = useRef<number | null>(null);
    const primaryPointerIdRef = useRef<number | null>(null);
    const startPosRef = useRef<{ x: number; y: number } | null>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    const isDraggingRef = useRef(false);
    const isTouchTargetingRef = useRef(false); // Track targeting mode for targetable cards
    const currentRotationRef = useRef(rotation);
    const lastTapTimeRef = useRef(0);
    const cardIdRef = useRef(card.id);

    // Sync external rotation
    useEffect(() => {
        setTouchRotation(rotation);
        currentRotationRef.current = rotation;
    }, [rotation]);

    // Sync isDragging to ref
    useEffect(() => {
        isDraggingRef.current = isTouchDragging;
    }, [isTouchDragging]);

    // Sync touchRotation to ref
    useEffect(() => {
        currentRotationRef.current = touchRotation;
    }, [touchRotation]);

    // Sync card id
    useEffect(() => {
        cardIdRef.current = card.id;
    }, [card.id]);

    // Listen for second finger at document level (for rotation during drag OR targeting)
    useEffect(() => {
        if (!isTouch) return;

        const handleDocumentPointerDown = (e: PointerEvent) => {
            const isInteracting = isDraggingRef.current || isTouchTargetingRef.current;
            // If we're interacting and this is a DIFFERENT pointer, rotate
            if (isInteracting && primaryPointerIdRef.current !== null && e.pointerId !== primaryPointerIdRef.current) {
                e.preventDefault();
                const newRot = currentRotationRef.current === 0 ? 180 : 0;
                console.log('[TouchCardWrapper] Document: Second finger - rotating to:', newRot);
                setTouchRotation(newRot);
                currentRotationRef.current = newRot;
                if (onRotationChange) onRotationChange(cardIdRef.current, newRot);
            }
        };

        document.addEventListener('pointerdown', handleDocumentPointerDown, { capture: true });
        return () => {
            document.removeEventListener('pointerdown', handleDocumentPointerDown, { capture: true });
        };
    }, [isTouch, onRotationChange]);

    // Watch for isTargetable changes - if targeting becomes invalid (e.g. rotation), cancel it
    useEffect(() => {
        if (!isTargetable && isTouchTargetingRef.current) {
            console.log('[TouchCardWrapper] Card no longer targetable - cancelling targeting');
            isTouchTargetingRef.current = false;
            if (onTouchTargetCancel) onTouchTargetCancel();
        }
    }, [isTargetable, onTouchTargetCancel]);

    const clearTimer = useCallback(() => {
        if (longPressTimerRef.current !== null) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
        if (previewTimerRef.current !== null) {
            clearTimeout(previewTimerRef.current);
            previewTimerRef.current = null;
        }
        setShowTouchPreview(false);
    }, []);

    const handlePointerDown = useCallback((e: React.PointerEvent) => {
        // Allow double-tap even when not interactive (for night burning)
        // BUT respect locked status (Psy-Block prevents burning)
        // Check for double-tap FIRST before blocking on isInteractive
        if (isTouch && onDoubleClick && !locked) {
            const now = Date.now();
            if (now - lastTapTimeRef.current < 300) {
                console.log('[TouchCardWrapper] Double-tap detected');
                clearTimer();
                // Create a synthetic mouse event for consistency
                const syntheticEvent = {
                    clientX: e.clientX,
                    clientY: e.clientY,
                    currentTarget: cardRef.current,
                    preventDefault: () => { },
                    stopPropagation: () => { },
                } as unknown as React.MouseEvent<HTMLDivElement>;
                onDoubleClick(card.id, syntheticEvent);
                lastTapTimeRef.current = 0;
                return;
            }
            lastTapTimeRef.current = now;
        }

        if (!isTouch || !isInteractive || locked || !isPlayable) {
            return;
        }

        const startX = e.clientX;
        const startY = e.clientY;

        // First finger: capture pointer (only if not already captured)
        if (primaryPointerIdRef.current === null) {
            primaryPointerIdRef.current = e.pointerId;
            startPosRef.current = { x: startX, y: startY };

            try {
                (e.target as HTMLElement).setPointerCapture(e.pointerId);
            } catch (err) {
                // Ignore capture errors
            }
        }

        // If this event is NOT from the primary pointer, ignore for drag/target start
        if (e.pointerId !== primaryPointerIdRef.current) return;

        // TARGETING MODE: For targetable cards (Attack, Disease, etc.) during combat
        // Start targeting immediately - no long-press wait needed
        // Also support 'hand' source for Mage spell modal targeting
        if (isTargetable && (source === 'prepared' || source === 'prepared-reaction' || source === 'hand') && onTouchTargetStart) {
            const cardRect = cardRef.current?.getBoundingClientRect();
            if (cardRect) {
                console.log('[TouchCardWrapper] Targetable card touch - starting targeting mode');
                isTouchTargetingRef.current = true;
                onTouchTargetStart(cardRect);
                // Set initial arrow position
                if (onTouchTargetMove) {
                    onTouchTargetMove(startX, startY);
                }
            }
            return; // Don't start long-press timer for targetable cards
        }

        // DRAG MODE: Standard long-press drag for non-targetable cards
        // HYBRID DEVICE FIX: If using a mouse, start drag immediately (bypass timer)
        // If using touch/pen, keep the timer to distinguish from scrolling
        if (e.pointerType === 'mouse') {
            console.log('[TouchCardWrapper] Mouse input detected - starting drag immediately');
            isDraggingRef.current = true;
            setIsTouchDragging(true);
            setDragPos({ x: startX, y: startY });
            if (onDragStartApp && source) {
                onDragStartApp(card, currentRotationRef.current, source);
            }
        } else {
            const isNight = (window as any).okoGamePhase === 'COMBAT' || (window as any).okoGamePhase === 'TOWER_PURCHASE';
            const dragDelay = (isNight || source === 'prepared-reaction') ? 50 : 250;
            longPressTimerRef.current = window.setTimeout(() => {
                console.log(`[TouchCardWrapper] Long press triggered - starting drag after ${dragDelay}ms`);
                isDraggingRef.current = true;
                setIsTouchDragging(true);
                setDragPos({ x: startX, y: startY });
                if (onDragStartApp && source) {
                    onDragStartApp(card, currentRotationRef.current, source);
                }
            }, dragDelay);
        }

        // Preview timer: shows card info after 1200ms of holding (700ms after drag starts)
        previewTimerRef.current = window.setTimeout(() => {
            console.log('[TouchCardWrapper] Preview timer triggered - showing card info');
            setShowTouchPreview(true);
        }, 1200);
    }, [isTouch, isInteractive, locked, isPlayable, isTargetable, onDragStartApp, card, source, onDoubleClick, clearTimer, onTouchTargetStart, onTouchTargetMove]);

    const handlePointerMove = useCallback((e: React.PointerEvent) => {
        if (!isTouch) return;
        if (primaryPointerIdRef.current !== e.pointerId) return;

        // TARGETING MODE: Update arrow position
        if (isTouchTargetingRef.current && onTouchTargetMove) {
            onTouchTargetMove(e.clientX, e.clientY);
            return;
        }

        // Cancel long press if moved too much before drag starts
        if (!isDraggingRef.current && startPosRef.current) {
            const dx = e.clientX - startPosRef.current.x;
            const dy = e.clientY - startPosRef.current.y;
            if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
                clearTimer();
                // Reset tap timer since this is a swipe
                lastTapTimeRef.current = 0;
            }
        }

        // Update drag position
        if (isDraggingRef.current) {
            setDragPos({ x: e.clientX, y: e.clientY });
        }
    }, [isTouch, clearTimer, onTouchTargetMove]);

    const handlePointerUp = useCallback((e: React.PointerEvent) => {
        if (!isTouch) return;
        if (e.pointerId !== primaryPointerIdRef.current) return;

        clearTimer();

        const dropX = e.clientX;
        const dropY = e.clientY;

        // TARGETING MODE: Complete targeting and check for valid target
        if (isTouchTargetingRef.current) {
            console.log('[TouchCardWrapper] Targeting release at:', { dropX, dropY });
            isTouchTargetingRef.current = false;
            if (onTouchTargetEnd) {
                onTouchTargetEnd(dropX, dropY);
            }
            primaryPointerIdRef.current = null;
            startPosRef.current = null;
            return;
        }

        // DRAG MODE: Handle drop
        const wasDragging = isDraggingRef.current;
        if (wasDragging) {
            console.log('[TouchCardWrapper] Drop at:', { dropX, dropY, rotation: currentRotationRef.current });
            isDraggingRef.current = false;
            setIsTouchDragging(false);
            setDragPos(null);

            if (onTouchDrop) {
                onTouchDrop(card, currentRotationRef.current, dropX, dropY);
            }

            if (onDragEndApp) onDragEndApp();
        }

        primaryPointerIdRef.current = null;
        startPosRef.current = null;
    }, [isTouch, clearTimer, onDragEndApp, onTouchDrop, onTouchTargetEnd, card]);

    // Render drag clone via portal
    const renderDragClone = () => {
        if (!isTouchDragging || !dragPos) return null;
        return ReactDOM.createPortal(
            <div
                style={{
                    position: 'fixed',
                    left: dragPos.x - 40,
                    top: dragPos.y - 60,
                    zIndex: 9999,
                    pointerEvents: 'none',
                    opacity: 0.9,
                    transform: `rotate(${touchRotation}deg) scale(1.1)`,
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.5))',
                }}
            >
                <Card
                    card={card}
                    rotation={0}
                    source={source}
                    isInteractive={false}
                    isPlayable={true}
                    locked={false}
                    language={language}
                />
            </div>,
            document.body
        );
    };

    // Render touch preview overlay when holding card
    const renderTouchPreview = () => {
        if (!showTouchPreview || !dragPos) return null;
        const cardName = getCardDisplayNameForCard(card, touchRotation, language);
        const essenceCost = card.essenceCost;
        const hasExtendedShield = card.extendedShield;

        return ReactDOM.createPortal(
            <div
                style={{
                    position: 'fixed',
                    left: dragPos.x,
                    top: dragPos.y - 120,
                    transform: 'translateX(-50%)',
                    zIndex: 10000,
                    pointerEvents: 'none',
                }}
            >
                <div className="bg-black/95 backdrop-blur-md rounded-xl px-4 py-3 border border-amber-500/50 shadow-2xl shadow-amber-500/20 animate-[fadeIn_0.2s_ease-out]">
                    <div className="text-amber-400 font-bold text-lg text-center whitespace-nowrap">
                        {cardName}
                    </div>
                    <div className="flex items-center justify-center gap-3 mt-1">
                        {essenceCost !== undefined && essenceCost > 0 && (
                            <div className="flex items-center gap-1 text-cyan-400">
                                <EssenceIcon className="w-4 h-4" />
                                <span className="text-sm font-semibold">{essenceCost}</span>
                            </div>
                        )}
                        {hasExtendedShield && (
                            <div className="flex items-center gap-1 text-blue-400">
                                <ShieldIcon className="w-4 h-4" />
                                <span className="text-xs">Reaction</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>,
            document.body
        );
    };

    // On non-touch devices, render Card normally with all handlers
    if (!isTouch) {
        return (
            <Card
                card={card}
                rotation={rotation}
                source={source}
                isInteractive={isInteractive}
                isPlayable={isPlayable}
                isTargetable={isTargetable}
                locked={locked}
                onRotationChange={onRotationChange}
                onDragStartApp={onDragStartApp}
                onDragEndApp={onDragEndApp}
                onDoubleClick={(id, e) => { if (onDoubleClick) onDoubleClick(id, e); }}
                language={language}
                {...cardProps}
            />
        );
    }

    return (
        <>
            <div
                ref={cardRef}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                style={{ touchAction: 'none', userSelect: 'none' }}
            >
                <Card
                    card={card}
                    rotation={touchRotation}
                    source={source}
                    isInteractive={false}
                    allowManualRotation={isInteractive}
                    isClickable={!isTouchDragging}
                    isPlayable={isPlayable}
                    locked={locked}
                    onRotationChange={onRotationChange}
                    language={language}
                    {...cardProps}
                />
            </div>
            {renderDragClone()}
            {renderTouchPreview()}
        </>
    );
};

export default TouchCardWrapper;
